/**
 * The book currently being written, remembered across pages.
 *
 * A 20-chapter book takes close to half an hour, and for that whole time
 * book_generations has no row for it — the row is only written once
 * generation completes. So /my-books is legitimately empty while you wait,
 * and nothing anywhere in the app says a book is coming.
 *
 * On 2026-09-19 a trial user hit exactly that: started a book at 18:04,
 * came back at 18:30 to an empty shelf, hunted through /my-books, /account
 * and /library, got signed out, and cancelled the trial at 18:32:17. The book
 * finished at 18:33:37 — 80 seconds later, 20 chapters, 36,000 words.
 *
 * This is the client's fast local copy so the banner paints immediately on a
 * reload. GET /api/generate/active is the authority, because localStorage is
 * per-tab and does not survive a logout — and that user was logged out twice
 * while their book was being written.
 */

const KEY = 'sai_active_generation';

export interface ActiveGeneration {
  sessionId: string;
  title: string;
  startedAt: number;
}

export function setActiveGeneration(sessionId: string, title: string): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ sessionId, title, startedAt: Date.now() }));
    // Same-tab listeners: the storage event only fires in OTHER tabs, so the
    // banner would not notice a generation starting in this one.
    window.dispatchEvent(new Event('sai:generation-changed'));
  } catch { /* private mode, blocked storage — the server poll still covers it */ }
}

export function getActiveGeneration(): ActiveGeneration | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (!v || typeof v.sessionId !== 'string') return null;
    return v as ActiveGeneration;
  } catch {
    return null;
  }
}

export function clearActiveGeneration(): void {
  try {
    localStorage.removeItem(KEY);
    window.dispatchEvent(new Event('sai:generation-changed'));
  } catch { /* ignore */ }
}
